import { BasicSymbolTable } from 'app/modules/rulesAdvanced/lib/SymbolTable';
import {
  CompositeQueryTreeNode,
  CompositeTypes,
  DmFact,
  DmFactTypes,
  DmObjectToFlag,
  FieldValue,
  IRSchema,
  MultiplePerspectivesAggregation,
  ValueTypes,
} from 'app/modules/rulesAdvanced/models';
import { Unit21DataClassifier } from 'app/modules/dataSettings/responses';
import { TIME_CONVERSION } from 'app/modules/rulesAdvanced/lib/IRHelpers';
import { RuleExecutionConfig } from 'app/modules/orgSettings/models';
import { MAX_WINDOW_SIZE_IN_YEARS_DEFAULT } from 'app/modules/rulesAdvanced/constants';
import { RuleSpecification } from 'app/modules/rules/types/responses';

export const constructOptionsFromSymbolTable = (table: BasicSymbolTable) => {
  return Object.keys(table).map((key) => {
    const symbol = table[key];
    const symbolKey =
      symbol.value.value.type === ValueTypes.FIELD
        ? symbol.value.value.label
        : key;
    return {
      value: {
        field:
          symbol.value.value.type === ValueTypes.FIELD
            ? symbol.value.value.field
            : key,
        symbolKey,
      },
      text: symbol.text,
      type: symbol.type,
    };
  });
};

/**
 * this checks the rule_condition to see if we have at least 1 proper expression
 * @param ruleCondition a binary tree of operands and operators like 'variable == 1', where (variable, 1) are operands and (==) is an operator
 * @returns boolean
 */
export const checkRuleConditionExists = (
  ruleCondition: CompositeQueryTreeNode,
): boolean => {
  // Note that only NOT_BETWEEN has a special data structure, where the BETWEEN clause is nested within a NOT clause, hence the special strict check.
  // Also note that NOT_BETWEEN has only 1 child operand, which is the between clause. See 'should interpret NOT_BETWEEN keyword' test for example
  return ruleCondition?.operator === 'NOT'
    ? ruleCondition?.operands?.length === 1 &&
        ruleCondition?.operands[0]?.type ===
          CompositeTypes.RULE_CONDITION_COMPOSITE &&
        ruleCondition?.operands[0]?.operator === 'BETWEEN'
    : ruleCondition?.operands?.length > 1; // this will be the first trigger condition someone writes in
};

export const getMaxWindowSizeInMinutes = (
  orgRuleExecutionConfig: RuleExecutionConfig,
): number => {
  if (!orgRuleExecutionConfig.max_window_size_in_days) {
    return TIME_CONVERSION.YEAR * MAX_WINDOW_SIZE_IN_YEARS_DEFAULT;
  }
  return orgRuleExecutionConfig.max_window_size_in_days * TIME_CONVERSION.DAY;
};

export const isIrSchema = (
  specification: RuleSpecification | undefined | null,
): specification is IRSchema => {
  return specification != null && !specification.type;
};

export const toIrSchema = (
  specification: RuleSpecification | undefined | null,
): IRSchema | null => {
  if (isIrSchema(specification)) {
    return specification;
  }
  return null;
};

export const toIrSchemaOrNull = (
  specification: RuleSpecification | undefined | null,
): IRSchema | null => {
  return toIrSchema(specification);
};

const ENTITY_TO_INSTRUMENT_FIELD = {
  sender_entity_id: 'sender_instrument_id',
  receiver_entity_id: 'receiver_instrument_id',
} as const satisfies Record<string, string>;

const INSTRUMENT_TO_ENTITY_FIELD = {
  sender_instrument_id: 'sender_entity_id',
  receiver_instrument_id: 'receiver_entity_id',
} as const satisfies Record<string, string>;

const ENTITY_VALUE_FACT_MODEL_MAP = {
  entity: 'txn_instrument',
  txn_instrument: 'entity',
} as const satisfies Record<string, string>;

function makePerspective(field: string): FieldValue {
  return {
    type: ValueTypes.FIELD,
    model: 'txn_event',
    field,
    datatype: 'text',
  };
}

function mapPerspective(
  perspective: FieldValue,
  targetObjectType: DmObjectToFlag,
): FieldValue[] {
  const mapping =
    targetObjectType === Unit21DataClassifier.INSTRUMENT
      ? ENTITY_TO_INSTRUMENT_FIELD
      : INSTRUMENT_TO_ENTITY_FIELD;

  const mappedField = mapping[perspective.field];
  if (mappedField) {
    return [{ ...perspective, field: mappedField }];
  }

  if (targetObjectType === Unit21DataClassifier.INSTRUMENT) {
    return [
      makePerspective('sender_instrument_id'),
      makePerspective('receiver_instrument_id'),
    ];
  }
  return [
    makePerspective('sender_entity_id'),
    makePerspective('receiver_entity_id'),
  ];
}

/**
 * Maps a single fact's perspectives when swapping object type between ENTITY
 * and INSTRUMENT. Sender/receiver perspectives are preserved 1:1; entity/
 * instrument references outside sender/receiver default to sender+receiver on
 * the target object type. Entity value facts have their field model remapped.
 */
export function mapFactForObjectSwap(
  fact: DmFact,
  targetObjectType: DmObjectToFlag,
): DmFact {
  if (fact.type === DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION) {
    const mpa = fact as MultiplePerspectivesAggregation;
    const mappedPerspectives: FieldValue[] = [];
    const seenFields = new Set<string>();

    for (const p of mpa.perspectives) {
      for (const mapped of mapPerspective(p, targetObjectType)) {
        if (!seenFields.has(mapped.field)) {
          seenFields.add(mapped.field);
          mappedPerspectives.push(mapped);
        }
      }
    }

    return {
      ...fact,
      object: targetObjectType,
      perspectives: mappedPerspectives,
    };
  }

  if (fact.type === DmFactTypes.ENTITY_VALUE_FACT) {
    const targetModel =
      ENTITY_VALUE_FACT_MODEL_MAP[fact.field.model] ?? fact.field.model;
    return {
      ...fact,
      object: targetObjectType,
      field: { ...fact.field, model: targetModel },
    };
  }

  return { ...fact, object: targetObjectType };
}
